import type { ReactElement, ReactNode } from 'react'
import type { TFunction } from 'i18next'
import { RefreshCw } from 'lucide-react'
import type { UpdateEndpointRequest } from '../../../../types/case-container'
import { RolesInput } from '../RolesInput'
import {
  DEVICE_TYPE_OPTIONS,
  HELP_CLASS,
  IMPORTANCE_OPTIONS,
  INPUT_CLASS,
  LABEL_CLASS,
  SELECT_CLASS,
  type Importance,
} from './edit-endpoint-modal.helpers'

export function ErrorBanner({ message }: { message: string }): ReactElement {
  return (
    <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/50">
      <p className="text-sm text-red-700 dark:text-red-400">{message}</p>
    </div>
  )
}

interface FieldShellProps {
  label: string
  help?: string
  children: ReactNode
}

function FieldShell({ label, help, children }: FieldShellProps): ReactElement {
  return (
    <div>
      <label className={LABEL_CLASS}>{label}</label>
      {children}
      {help !== undefined && <p className={HELP_CLASS}>{help}</p>}
    </div>
  )
}

interface TextFieldProps {
  label: string
  value: string
  placeholder?: string
  required?: boolean
  type?: 'text' | 'textarea'
  rows?: number
  onChange: (value: string) => void
}

function TextField(props: TextFieldProps): ReactElement {
  const { label, value, placeholder, required, type = 'text', rows, onChange } = props
  return (
    <FieldShell label={label}>
      {type === 'textarea' ? (
        <textarea
          value={value}
          onChange={(event) => {
            onChange(event.target.value)
          }}
          rows={rows}
          className={INPUT_CLASS}
          placeholder={placeholder}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(event) => {
            onChange(event.target.value)
          }}
          className={INPUT_CLASS}
          placeholder={placeholder}
          required={required}
        />
      )}
    </FieldShell>
  )
}

interface SetterProps {
  formData: UpdateEndpointRequest
  setFormData: (next: UpdateEndpointRequest) => void
  t: TFunction<'cases'>
}

export function HostnameField({ formData, setFormData, t }: SetterProps): ReactElement {
  return (
    <TextField
      label="Hostname *"
      value={formData.hostname ?? ''}
      placeholder={t('infrastructure.placeholders.hostname')}
      required
      onChange={(value) => {
        setFormData({ ...formData, hostname: value })
      }}
    />
  )
}

export function DeviceTypeField({ formData, setFormData }: SetterProps): ReactElement {
  return (
    <FieldShell label="Device Type">
      <select
        value={formData.deviceType ?? 'workstation'}
        onChange={(event) => {
          setFormData({ ...formData, deviceType: event.target.value })
        }}
        className={SELECT_CLASS}
      >
        {DEVICE_TYPE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldShell>
  )
}

export function IpAddressField({ formData, setFormData, t }: SetterProps): ReactElement {
  return (
    <TextField
      label="IP Address"
      value={formData.ipAddresses?.[0] ?? ''}
      placeholder={t('infrastructure.placeholders.ipAddress')}
      onChange={(value) => {
        setFormData({ ...formData, ipAddresses: value ? [value] : [] })
      }}
    />
  )
}

export function DomainField({ formData, setFormData, t }: SetterProps): ReactElement {
  return (
    <TextField
      label="Domain"
      value={formData.domain ?? ''}
      placeholder={t('infrastructure.placeholders.domainName')}
      onChange={(value) => {
        setFormData({ ...formData, domain: value })
      }}
    />
  )
}

export function OsFieldsRow({ formData, setFormData, t }: SetterProps): ReactElement {
  return (
    <div className="grid grid-cols-2 gap-4">
      <TextField
        label="Operating System"
        value={formData.operatingSystem ?? ''}
        placeholder={t('infrastructure.placeholders.osType')}
        onChange={(value) => {
          setFormData({ ...formData, operatingSystem: value })
        }}
      />
      <TextField
        label="OS Version"
        value={formData.osVersion ?? ''}
        placeholder={t('infrastructure.placeholders.osVersion')}
        onChange={(value) => {
          setFormData({ ...formData, osVersion: value })
        }}
      />
    </div>
  )
}

export function OuPathField({ formData, setFormData, t }: SetterProps): ReactElement {
  return (
    <TextField
      label="OU Path"
      value={formData.ouPath ?? ''}
      placeholder={t('infrastructure.placeholders.distinguishedName')}
      onChange={(value) => {
        setFormData({ ...formData, ouPath: value })
      }}
    />
  )
}

export function ImportanceField({ formData, setFormData }: SetterProps): ReactElement {
  return (
    <FieldShell label="Importance" help="Priority level for collection and analysis focus">
      <select
        value={formData.importance ?? 'unknown'}
        onChange={(event) => {
          setFormData({ ...formData, importance: event.target.value as Importance })
        }}
        className={SELECT_CLASS}
      >
        {IMPORTANCE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldShell>
  )
}

interface RolesFieldProps extends SetterProps {
  existingCustomRoles: string[]
}

export function RolesField({
  formData,
  setFormData,
  existingCustomRoles,
}: RolesFieldProps): ReactElement {
  return (
    <FieldShell label="Roles" help="Functional roles for this IT system (e.g., AD, DNS, DHCP)">
      <RolesInput
        value={formData.roles ?? []}
        onChange={(roles) => {
          setFormData({ ...formData, roles })
        }}
        existingCustomRoles={existingCustomRoles}
      />
    </FieldShell>
  )
}

export function NotesField({ formData, setFormData, t }: SetterProps): ReactElement {
  return (
    <TextField
      label="Notes"
      type="textarea"
      rows={3}
      value={formData.notes ?? ''}
      placeholder={t('infrastructure.placeholders.endpointNotes')}
      onChange={(value) => {
        setFormData({ ...formData, notes: value })
      }}
    />
  )
}

interface FormActionsProps {
  onClose: () => void
  updating: boolean
  canSubmit: boolean
}

export function FormActions({ onClose, updating, canSubmit }: FormActionsProps): ReactElement {
  return (
    <div className="flex justify-end gap-3 pt-4">
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={updating || !canSubmit}
        className="inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white hover:bg-[#5c6e94] disabled:opacity-50"
      >
        {updating && <RefreshCw className="h-4 w-4 animate-spin" />}
        Save Changes
      </button>
    </div>
  )
}
